import { useCallback, useEffect, useState } from "react";
import { BookOpen, CircleHelp, Loader2, RefreshCw, Search, Sparkles, SearchX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { clientRequestHeaders } from "@/lib/sm-api";
import { resolveHelpArticleById } from "@/lib/product-knowledge/contextual-help";

type HelpAnalyticsSummary = {
  topArticles: { articleId: string; views: number }[];
  topSearches: { query: string; count: number }[];
  noResultSearches: { query: string; count: number }[];
  tourCompletions: { tourId: string; completed: number; skipped: number }[];
  totalEvents: number;
  distinctTenants: number;
  renata: {
    conversations: number;
    distinctTenants: number;
    totalMessages: number;
    errorRate: number | null;
    avgDurationMs: number | null;
    topSkills: { skillId: string; count: number }[];
  };
};

function articleLabel(id: string): string {
  return resolveHelpArticleById(id)?.title ?? id;
}

export function HelpAnalyticsPanel() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState<HelpAnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/help-analytics?days=${days}`, {
        credentials: "include",
        headers: clientRequestHeaders(),
      });
      const body = (await res.json()) as HelpAnalyticsSummary & { error?: string };
      if (!res.ok) {
        setError(body.error ?? "No autorizado");
        setData(null);
        return;
      }
      setData(body);
    } catch {
      setError("Error de red");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Card className="glass-card border-border/60">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <CircleHelp className="h-4 w-4 text-primary" />
            Help & Adoption Analytics
          </CardTitle>
          <CardDescription>
            Documentación, búsquedas, tours y uso de Renata/IA — demos/seed excluidos.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="h-8 w-[110px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 días</SelectItem>
              <SelectItem value="30">30 días</SelectItem>
              <SelectItem value="90">90 días</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : loading && !data ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : data ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg border border-border/50 bg-secondary/10 p-3">
                <p className="text-xs text-muted-foreground">Eventos ({days}d)</p>
                <p className="text-xl font-semibold tabular-nums">{data.totalEvents.toLocaleString("es-AR")}</p>
              </div>
              <div className="rounded-lg border border-border/50 bg-secondary/10 p-3">
                <p className="text-xs text-muted-foreground">Tenants activos (Help)</p>
                <p className="text-xl font-semibold tabular-nums">{data.distinctTenants}</p>
              </div>
              <div className="rounded-lg border border-border/50 bg-secondary/10 p-3">
                <p className="text-xs text-muted-foreground">Búsquedas sin resultado</p>
                <p className="text-xl font-semibold tabular-nums">
                  {data.noResultSearches.reduce((s, r) => s + r.count, 0)}
                </p>
              </div>
              <div className="rounded-lg border border-border/50 bg-secondary/10 p-3">
                <p className="text-xs text-muted-foreground">Tours completados / saltados</p>
                <p className="text-xl font-semibold tabular-nums">
                  {data.tourCompletions.reduce((s, r) => s + r.completed, 0)} /{" "}
                  {data.tourCompletions.reduce((s, r) => s + r.skipped, 0)}
                </p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <RankList
                title="Artículos más vistos"
                icon={BookOpen}
                empty="Sin vistas registradas"
                items={data.topArticles.map((a) => ({
                  key: a.articleId,
                  label: articleLabel(a.articleId),
                  count: a.views,
                }))}
              />
              <RankList
                title="Búsquedas frecuentes"
                icon={Search}
                empty="Sin búsquedas"
                items={data.topSearches.map((s) => ({
                  key: s.query,
                  label: s.query,
                  count: s.count,
                }))}
              />
              <RankList
                title="Búsquedas sin resultado (fricción)"
                icon={SearchX}
                empty="Todas las búsquedas encontraron resultados"
                items={data.noResultSearches.map((s) => ({
                  key: s.query,
                  label: s.query,
                  count: s.count,
                }))}
              />
            </div>

            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <CircleHelp className="h-3.5 w-3.5" /> Tours (completados vs. saltados)
              </p>
              {data.tourCompletions.length === 0 ? (
                <p className="text-xs text-muted-foreground">Sin datos de tours en el período.</p>
              ) : (
                <ul className="space-y-1.5">
                  {data.tourCompletions.map((t) => (
                    <li key={t.tourId} className="flex items-center justify-between text-sm">
                      <span className="truncate">{t.tourId}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {t.completed} completados · {t.skipped} saltados
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
              <p className="mb-3 flex items-center gap-1.5 text-sm font-semibold">
                <Sparkles className="h-4 w-4 text-primary" /> Renata / IA — métrica separada de Help
              </p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div>
                  <p className="text-xs text-muted-foreground">Conversaciones</p>
                  <p className="text-lg font-semibold tabular-nums">{data.renata.conversations}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tenants usando Renata</p>
                  <p className="text-lg font-semibold tabular-nums">{data.renata.distinctTenants}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Mensajes totales</p>
                  <p className="text-lg font-semibold tabular-nums">{data.renata.totalMessages}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tasa de error</p>
                  <p className="text-lg font-semibold tabular-nums">
                    {data.renata.errorRate != null ? `${data.renata.errorRate}%` : "—"}
                  </p>
                </div>
              </div>
              {data.renata.topSkills.length > 0 && (
                <div className="mt-3">
                  <p className="mb-1 text-xs text-muted-foreground">Skills más usadas</p>
                  <ul className="space-y-1 text-sm">
                    {data.renata.topSkills.map((s) => (
                      <li key={s.skillId} className="flex justify-between">
                        <span>{s.skillId}</span>
                        <span className="text-muted-foreground">{s.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function RankList({
  title,
  icon: Icon,
  empty,
  items,
}: {
  title: string;
  icon: typeof BookOpen;
  empty: string;
  items: { key: string; label: string; count: number }[];
}) {
  return (
    <div className="rounded-lg border border-border/50 bg-secondary/10 p-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </p>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ol className="space-y-1.5">
          {items.map((item, i) => (
            <li key={item.key} className="flex items-start justify-between gap-2 text-sm">
              <span className="min-w-0 truncate text-foreground" title={item.label}>
                <span className="mr-1.5 text-muted-foreground">{i + 1}.</span>
                {item.label}
              </span>
              <span className="shrink-0 tabular-nums text-xs font-medium text-muted-foreground">{item.count}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
